import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTableControls, type Column } from '../lib/useTableControls'
import { TableHead } from '../components/TableControls'
import { DetailGrid, ExpandableRow, useOpenRow } from '../components/RowDetail'
import { Drillable, DrillValue, useDrill } from '../components/Drill'
import type { ExploreRef } from '../lib/adminClient'
import {
  getAiOverview,
  listAiUsage,
  listAiUsageLog,
  type RemoteAiOverview,
  type RemoteAiAccount,
  type RemoteAiModelRow,
  type RemoteAiTierUsage,
  type RemoteAiUsageEntry
} from '../lib/adminClient'
import { CreditsModal, UsageLogTable } from '../components/AccountCredits'
import { isMissingEndpoint, LoadError } from '../components/LoadError'
import { useAuth } from '../lib/auth'
import { credits as fmtCredits, dateTime, marginPct, num, pct, usd, usdFine } from '../lib/format'
import {
  CREDIT_BUCKETS,
  DEFAULT_CREDIT_MARKUP,
  TIER_LADDER,
  TOPUP_PACKS,
  tierName,
  tierTone,
  usdToCredits
} from '../lib/tiers'

// One page for the whole managed-AI (PlexiBrain) picture, in CREDITS since the
// growth change: 1 credit = US$0.01 of billed usage, billed = Anthropic list
// cost x AI_CREDIT_MARKUP (default 2.0). The overview is /admin/ai-overview;
// per-account balances are /admin/ai-usage; the call ledger is
// /admin/ai-usage/log. Every number is the server's. A section whose data the
// server does not report yet says so by name; it never shows a zero in its
// place.

// A legacy server reports the credit ledger in billed dollars. Converting at
// 100 credits per billed dollar is the definition of a credit, not an estimate.
function creditsOf(credits: number | null | undefined, legacyUsd: number | null | undefined): number | null {
  if (typeof credits === 'number') return credits
  if (typeof legacyUsd === 'number') return usdToCredits(legacyUsd)
  return null
}

// The records behind the page's figures (src/adminExplore.ts on the server).
const ALL_CALLS: ExploreRef = { set: 'aiCalls', filters: {} }
const ALL_CREDITS: ExploreRef = { set: 'aiCredits', filters: {} }
const grants = (ids: string, kind: string): ExploreRef => ({ set: 'creditBuckets', filters: { accounts: ids, kind, live: '1' } })

type DailyKey = 'calls' | 'rawCostUsd' | 'billedUsd'

const DAILY_META: Record<DailyKey, { label: string; tone: string }> = {
  calls: { label: 'Calls', tone: '#22d3ee' },
  rawCostUsd: { label: 'Anthropic cost', tone: '#f472b6' },
  billedUsd: { label: 'Billed', tone: '#8b5cf6' }
}

function sparkline(values: number[], color: string, width = 640, height = 140, onPick?: (i: number) => void, labels?: string[]) {
  if (values.length < 2) return null
  const max = Math.max(...values)
  const min = Math.min(...values)
  const span = max - min || 1
  const stepX = width / (values.length - 1)
  const points = values.map(
    (v, i) => [i * stepX, height - ((v - min) / span) * (height - 12) - 6] as const
  )
  const path = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
  const area = `${path} L${width},${height} L0,${height} Z`
  const id = `ai-g-${color.replace('#', '')}`
  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="overflow-visible">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.32" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${id})`} />
      <path d={path} fill="none" stroke={color} strokeWidth="2" />
      {points.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={i === points.length - 1 ? 3 : 0} fill={color} />
      ))}
      {/* A day-wide strip over each point: click it for that day's calls. */}
      {onPick &&
        points.map(([x], i) => (
          <rect
            key={`hit-${i}`}
            x={i === 0 ? 0 : x - stepX / 2}
            y={0}
            width={(i === 0 || i === points.length - 1 ? stepX / 2 : stepX)}
            height={height}
            fill="transparent"
            className="cursor-pointer hover:fill-white/5"
            onClick={() => onPick(i)}
            data-testid={`ai-chart-day-${labels?.[i] ?? i}`}
          >
            <title>{`${labels?.[i] ?? ''}: click for that day's calls`}</title>
          </rect>
        ))}
    </svg>
  )
}

function Tile({ label, value, hint, testId }: { label: string; value: string; hint?: string; testId?: string }): JSX.Element {
  return (
    <div className="glass-card rounded-xl p-4 border border-white/5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">{label}</div>
      <div className="text-xl font-semibold mt-1 tabular-nums">{value}</div>
      {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
    </div>
  )
}

function SectionHead({ title, blurb }: { title: string; blurb?: string }): JSX.Element {
  return (
    <div className="mt-10 mb-3">
      <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400">{title}</div>
      {blurb && <div className="text-sm text-slate-400 mt-0.5">{blurb}</div>}
    </div>
  )
}

function NotYet({ endpoint, what, testId }: { endpoint: string; what: string; testId?: string }): JSX.Element {
  return (
    <div className="glass-card rounded-xl p-5 text-sm text-amber-200" data-testid={testId}>
      Not reported by this server yet: {what}. It needs <code className="text-amber-100">{endpoint}</code> (growth spec).
    </div>
  )
}

function OverviewSection({ overview }: { overview: RemoteAiOverview }): JSX.Element {
  const [dailyKey, setDailyKey] = useState<DailyKey>('billedUsd')
  const dailyValues = useMemo(() => overview.daily.map((d) => d[dailyKey]), [overview, dailyKey])
  const { totals, credits, byModel } = overview
  const modelRow = useOpenRow()
  const dailyMeta = DAILY_META[dailyKey]
  const balance = creditsOf(credits.balance, credits.balanceUsd)
  const granted = creditsOf(credits.granted, credits.grantedUsd)
  const spent = creditsOf(credits.spent, credits.spentUsd)
  const drill = useDrill()

  return (
    <div className="space-y-6">
      {/* The server's totals and per-model rows are lifetime figures; only the
          daily series is windowed. Labelled as such. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Drillable to={ALL_CALLS} label="Metered calls, all time" expect={{ key: 'count', value: totals.calls }} testId="ai-drill-calls">
          <Tile label="Metered calls (all time)" value={num(totals.calls)} />
        </Drillable>
        <Drillable to={ALL_CALLS} label="Anthropic cost, all time" expect={{ key: 'rawCostUsd', value: totals.rawCostUsd }} testId="ai-drill-cost">
          <Tile label="Anthropic cost (all time)" value={usd(totals.rawCostUsd)} hint="What Plexii pays" testId="ai-raw-cost" />
        </Drillable>
        <Drillable to={ALL_CALLS} label="Billed, all time" expect={{ key: 'billedUsd', value: totals.billedUsd }}>
          <Tile
            label="Billed (all time)"
            value={usd(totals.billedUsd)}
            hint={`${fmtCredits(usdToCredits(totals.billedUsd))} debited`}
            testId="ai-billed"
          />
        </Drillable>
        <Drillable to={ALL_CALLS} label="Calls behind the gross margin">
        <Tile
          label="Gross margin"
          value={marginPct(totals.rawCostUsd, totals.billedUsd)}
          hint={
            typeof overview.markup === 'number'
              ? `Markup ${overview.markup}x on Anthropic list cost`
              : `Markup not reported (spec default ${DEFAULT_CREDIT_MARKUP}x)`
          }
          testId="ai-margin"
        />
        </Drillable>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Drillable to={ALL_CREDITS} label="Accounts with credits" expect={{ key: 'count', value: credits.accountsWithCredits }}>
          <Tile label="Accounts with credits" value={num(credits.accountsWithCredits)} />
        </Drillable>
        <Drillable to={ALL_CREDITS} label="Credits granted" expect={granted === null ? undefined : { key: 'granted', value: granted }} testId="ai-drill-granted">
          <Tile label="Credits granted" value={fmtCredits(granted)} hint="Allowance, bonus and purchased" />
        </Drillable>
        {/* A current server works this out from the grants not yet expired
            (credits.balance), so it opens those grants; an older one sends
            only the per-account summary, which opens those rows. */}
        <Drillable
          to={typeof credits.balance === 'number' ? { set: 'creditBuckets', filters: { live: '1', accountOwned: '1' } } : ALL_CREDITS}
          label="Credits outstanding"
          expect={balance === null ? undefined : { key: typeof credits.balance === 'number' ? 'remaining' : 'balance', value: balance }}
          testId="ai-drill-outstanding"
        >
          <Tile label="Credits outstanding" value={fmtCredits(balance)} hint="Unspent across all accounts" testId="ai-credits-outstanding" />
        </Drillable>
        <Drillable to={ALL_CREDITS} label="Credits spent" expect={spent === null ? undefined : { key: 'spent', value: spent }}>
          <Tile label="Credits spent" value={fmtCredits(spent)} />
        </Drillable>
      </div>

      <div className="glass-card rounded-xl p-6">
        <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400">Daily trend</div>
            <div className="text-sm text-slate-400 mt-0.5">{dailyMeta.label} per day, last 30 days (days with usage).</div>
          </div>
          <div className="flex gap-2">
            {(Object.keys(DAILY_META) as DailyKey[]).map((k) => (
              <button
                key={k}
                onClick={() => setDailyKey(k)}
                className={`px-3 py-1.5 rounded-md text-xs border ${
                  dailyKey === k ? 'border-accent/60 bg-accent/15 text-white' : 'border-white/10 text-slate-300 hover:border-white/20'
                }`}
              >
                {DAILY_META[k].label}
              </button>
            ))}
          </div>
        </div>
        {dailyValues.length < 2 ? (
          <div className="text-sm text-slate-500">Not enough daily data to draw a trend yet.</div>
        ) : (
          <>
            <div className="h-36">
              {sparkline(
                dailyValues,
                dailyMeta.tone,
                640,
                140,
                (i) => {
                  const d = overview.daily[i]
                  drill.open({ set: 'aiCalls', filters: { on: d.date } }, { label: `AI calls on ${d.date}`, expect: { key: dailyKey === 'calls' ? 'count' : dailyKey, value: d[dailyKey] } })
                },
                overview.daily.map((d) => d.date)
              )}
            </div>
            <div className="flex justify-between text-[10px] text-slate-500 mt-2">
              <span>{overview.daily[0]?.date}</span>
              <span>{overview.daily[overview.daily.length - 1]?.date}</span>
            </div>
          </>
        )}
      </div>

      <div className="glass-card rounded-xl overflow-hidden">
        <div className="px-4 pt-4 text-[11px] uppercase tracking-[0.16em] text-slate-400">By model (all time)</div>
        {byModel.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No model usage recorded.</div>
        ) : (
          <table className="admin-table mt-3" data-testid="ai-by-model">
            <thead>
              <tr>
                <th>Model</th>
                <th className="text-right">Calls</th>
                <th className="text-right">Input tokens</th>
                <th className="text-right">Output tokens</th>
                <th className="text-right">Anthropic cost</th>
                <th className="text-right">Billed</th>
                <th className="text-right">Credits</th>
                <th className="text-right">Margin</th>
              </tr>
            </thead>
            <tbody>
              {byModel.map((m) => (
                <ExpandableRow
                  key={m.model}
                  open={modelRow.isOpen(m.model)}
                  onToggle={() => modelRow.toggle(m.model)}
                  colSpan={8}
                  testId={`ai-model-${m.model}`}
                  detail={() => <ModelDetail m={m} totals={totals} />}
                >
                  <td className="font-mono text-[11px] text-slate-100">{m.model}</td>
                  <td className="text-right tabular-nums">
                    <DrillValue to={{ set: 'aiCalls', filters: { model: m.model } }} label={`${m.model} calls`} expect={{ key: 'count', value: m.calls }} testId={`ai-model-calls-${m.model}`}>{num(m.calls)}</DrillValue>
                  </td>
                  <td className="text-right tabular-nums">
                    <DrillValue to={{ set: 'aiCalls', filters: { model: m.model } }} label={`${m.model} input tokens`} expect={{ key: 'inputTokens', value: m.inputTokens }}>{num(m.inputTokens)}</DrillValue>
                  </td>
                  <td className="text-right tabular-nums">
                    <DrillValue to={{ set: 'aiCalls', filters: { model: m.model } }} label={`${m.model} output tokens`} expect={{ key: 'outputTokens', value: m.outputTokens }}>{num(m.outputTokens)}</DrillValue>
                  </td>
                  <td className="text-right tabular-nums">
                    <DrillValue to={{ set: 'aiCalls', filters: { model: m.model } }} label={`${m.model} Anthropic cost`} expect={{ key: 'rawCostUsd', value: m.rawCostUsd }}>{usd(m.rawCostUsd)}</DrillValue>
                  </td>
                  <td className="text-right tabular-nums">
                    <DrillValue to={{ set: 'aiCalls', filters: { model: m.model } }} label={`${m.model} billed`} expect={{ key: 'billedUsd', value: m.billedUsd }}>{usd(m.billedUsd)}</DrillValue>
                  </td>
                  <td className="text-right tabular-nums">{num(usdToCredits(m.billedUsd))}</td>
                  <td className="text-right tabular-nums">{marginPct(m.rawCostUsd, m.billedUsd)}</td>
                </ExpandableRow>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function TierAllowanceSection({ overview }: { overview: RemoteAiOverview }): JSX.Element {
  const tierRow = useOpenRow()
  if (!overview.byTier) {
    return <NotYet endpoint="byTier on GET /admin/ai-overview" what="per-tier allowance usage" testId="ai-tier-missing" />
  }
  if (overview.byTier.length === 0) {
    return <div className="glass-card rounded-xl p-5 text-sm text-slate-500">No allowance issued this period.</div>
  }
  return (
    <div className="glass-card rounded-xl overflow-hidden">
      <table className="admin-table" data-testid="ai-tier-usage">
        <thead>
          <tr>
            <th>Tier</th>
            <th className="text-right">Accounts</th>
            <th className="text-right">Allowance per account</th>
            <th className="text-right">Allowance issued</th>
            <th className="text-right">Allowance used</th>
            <th className="text-right">Used</th>
            <th className="text-right">Bonus used</th>
            <th className="text-right">Purchased used</th>
          </tr>
        </thead>
        <tbody>
          {overview.byTier.map((t) => {
            const spec = TIER_LADDER.find((s) => s.id === t.tier)
            // An older server sends no account ids; the figures then stay plain.
            const ids = t.accountIds ? t.accountIds.join(',') : null
            return (
              <ExpandableRow
                key={t.tier}
                open={tierRow.isOpen(t.tier)}
                onToggle={() => tierRow.toggle(t.tier)}
                colSpan={8}
                testId={`ai-tier-${t.tier}`}
                detail={() => <TierDetail t={t} />}
              >
                <td>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${tierTone(t.tier)}`}>{tierName(t.tier)}</span>
                </td>
                <td className="text-right tabular-nums">
                  {ids === null ? num(t.accounts) : (
                    <DrillValue to={{ set: 'accounts', filters: { ids } }} label={`${tierName(t.tier)} accounts with AI usage or credits`} expect={{ key: 'count', value: t.accounts }}>{num(t.accounts)}</DrillValue>
                  )}
                </td>
                <td className="text-right tabular-nums text-slate-400">
                  {spec?.allowanceCredits != null ? `${num(spec.allowanceCredits)}${spec.unit === 'seat' ? ' / seat' : ''}` : 'custom'}
                </td>
                <td className="text-right tabular-nums">
                  {ids === null ? num(t.allowanceCredits) : (
                    <DrillValue to={grants(ids, 'allowance')} label={`${tierName(t.tier)} allowance issued`} expect={{ key: 'granted', value: t.allowanceCredits }} testId={`ai-tier-allowance-${t.tier}`}>{num(t.allowanceCredits)}</DrillValue>
                  )}
                </td>
                <td className="text-right tabular-nums">
                  {ids === null ? num(t.allowanceUsedCredits) : (
                    <DrillValue to={grants(ids, 'allowance')} label={`${tierName(t.tier)} allowance used`} expect={{ key: 'used', value: t.allowanceUsedCredits }}>{num(t.allowanceUsedCredits)}</DrillValue>
                  )}
                </td>
                <td className="text-right tabular-nums">{pct(t.allowanceUsedCredits, t.allowanceCredits)}</td>
                <td className="text-right tabular-nums">
                  {ids === null || t.bonusUsedCredits === undefined ? num(t.bonusUsedCredits) : (
                    <DrillValue to={grants(ids, 'bonus')} label={`${tierName(t.tier)} bonus credits used`} expect={{ key: 'used', value: t.bonusUsedCredits }}>{num(t.bonusUsedCredits)}</DrillValue>
                  )}
                </td>
                <td className="text-right tabular-nums">
                  {ids === null || t.purchasedUsedCredits === undefined ? num(t.purchasedUsedCredits) : (
                    <DrillValue to={grants(ids, 'purchased')} label={`${tierName(t.tier)} purchased credits used`} expect={{ key: 'used', value: t.purchasedUsedCredits }}>{num(t.purchasedUsedCredits)}</DrillValue>
                  )}
                </td>
              </ExpandableRow>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function TopupSection({ overview }: { overview: RemoteAiOverview }): JSX.Element {
  const packs = <div className="text-xs text-slate-500 mt-2">Packs: {TOPUP_PACKS.map((p) => `$${p.usd} → ${num(p.credits)}`).join(' · ')}. Purchased credits expire after 12 months.</div>
  if (!overview.topups) {
    return (
      <div>
        <NotYet endpoint="topups on GET /admin/ai-overview" what="top-up revenue" testId="ai-topups-missing" />
        {packs}
      </div>
    )
  }
  const t = overview.topups
  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ai-topups">
        <Drillable to={{ set: 'funnel', filters: { event: 'topup_purchased' } }} label="Top-up revenue" expect={{ key: 'amount', value: t.revenueUsd }} testId="ai-drill-topup-revenue">
          <Tile label="Top-up revenue" value={usd(t.revenueUsd)} />
        </Drillable>
        <Drillable to={{ set: 'creditBuckets', filters: { source: 'topup' } }} label="Top-ups" expect={{ key: 'count', value: t.count }}>
          <Tile label="Top-ups" value={num(t.count)} />
        </Drillable>
        <Drillable to={{ set: 'creditBuckets', filters: { source: 'topup' } }} label="Credits sold" expect={{ key: 'granted', value: t.credits }}>
          <Tile label="Credits sold" value={fmtCredits(t.credits)} />
        </Drillable>
        <Drillable to={{ set: 'funnel', filters: { event: 'topup_purchased' } }} label="Top-up purchases behind the average">
          <Tile label="Average top-up" value={t.count ? usd(t.revenueUsd / t.count) : '—'} />
        </Drillable>
      </div>
      {t.byPack && t.byPack.length > 0 && (
        <div className="text-xs text-slate-400 mt-2">
          By pack: {t.byPack.map((p) => `$${p.usd} × ${num(p.count)}`).join(' · ')}
        </div>
      )}
      {packs}
    </div>
  )
}

function UsageLogSection(): JSX.Element {
  const [state, setState] = useState<
    { s: 'loading' } | { s: 'ready'; entries: RemoteAiUsageEntry[] } | { s: 'missing' } | { s: 'error'; error: string }
  >({ s: 'loading' })
  useEffect(() => {
    let cancelled = false
    void listAiUsageLog({ limit: 100 }).then((res) => {
      if (cancelled) return
      if (res.ok) setState({ s: 'ready', entries: res.data.entries ?? [] })
      else if (isMissingEndpoint(res.status, res.error)) setState({ s: 'missing' })
      else setState({ s: 'error', error: res.error })
    })
    return () => {
      cancelled = true
    }
  }, [])
  if (state.s === 'loading') return <div className="glass-card rounded-xl p-5 text-sm text-slate-400">Loading usage log…</div>
  if (state.s === 'missing') return <NotYet endpoint="GET /admin/ai-usage/log" what="the per-call usage log" testId="ai-log-missing" />
  if (state.s === 'error') return <LoadError what="the AI usage log" endpoint="GET /admin/ai-usage/log" error={state.error} />
  if (state.entries.length === 0) return <div className="glass-card rounded-xl p-5 text-sm text-slate-500">No metered calls recorded.</div>
  return (
    <div className="glass-card rounded-xl overflow-hidden">
      <UsageLogTable entries={state.entries} showAccount />
    </div>
  )
}

function accountName(a: RemoteAiAccount): string {
  return a.displayName || (a.handle ? `@${a.handle}` : a.email || a.accountId)
}

function AccountsSection(): JSX.Element {
  const session = useAuth()
  const [accounts, setAccounts] = useState<RemoteAiAccount[] | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState<RemoteAiAccount | null>(null)
  const accountRow = useOpenRow()

  async function load(): Promise<void> {
    const res = await listAiUsage()
    if (res.ok) {
      setAccounts(res.data.accounts)
      setState('ready')
    } else {
      setError(res.error)
      setState('error')
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const visible = useMemo(() => {
    const all = accounts ?? []
    const q = query.trim().toLowerCase()
    if (!q) return all
    return all.filter(
      (a) =>
        (a.email?.toLowerCase().includes(q) ?? false) ||
        (a.handle?.toLowerCase().includes(q) ?? false) ||
        (a.displayName?.toLowerCase().includes(q) ?? false) ||
        a.accountId.toLowerCase().includes(q)
    )
  }, [accounts, query])

  const t = useTableControls(visible, AI_ACCOUNT_COLUMNS, 'ai-accounts')

  if (state === 'loading') {
    return <div className="glass-card rounded-xl p-6 text-sm text-slate-400">Loading account spend…</div>
  }
  if (state === 'error') {
    return <LoadError what="per-account AI spend" endpoint="GET /admin/ai-usage" error={error ?? 'unknown error'} />
  }

  const all = accounts ?? []

  return (
    <div>
      <div className="glass-card rounded-xl p-4 mb-4 flex flex-wrap gap-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search email, handle, name, or account id…"
          className="flex-1 min-w-[260px] bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent"
        />
      </div>

      {all.length === 0 ? (
        <div className="glass-card rounded-xl p-6 text-sm text-slate-400">No account AI usage or credits recorded yet.</div>
      ) : visible.length === 0 ? (
        <div className="glass-card rounded-xl p-6 text-sm text-slate-400">No accounts match your search.</div>
      ) : (
        <div className="glass-card rounded-xl overflow-hidden">
          <table className="admin-table" data-testid="ai-accounts">
            <TableHead t={t} />
            <tbody>
              {t.rows.map((a) => {
                const bal = creditsOf(a.balance, a.balanceUsd)
                return (
                  <ExpandableRow
                    key={a.accountId}
                    open={accountRow.isOpen(a.accountId)}
                    onToggle={() => accountRow.toggle(a.accountId)}
                    colSpan={AI_ACCOUNT_COLUMNS.length}
                    testId={`ai-account-${a.accountId}`}
                    detail={() => <AccountDetail a={a} onCredits={() => setOpen(a)} />}
                  >
                    <td>
                      <div className="font-medium text-slate-100">
                        <DrillValue to={{ set: 'accounts', filters: { id: a.accountId } }} label={`Account ${a.email ?? a.accountId}`}>{accountName(a)}</DrillValue>
                      </div>
                      <div className="text-[11px] text-slate-500">{a.email ?? a.accountId}</div>
                    </td>
                    <td>
                      {a.tier ? (
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${tierTone(a.tier)}`}>{tierName(a.tier)}</span>
                      ) : (
                        <span className="text-[11px] text-slate-600">—</span>
                      )}
                    </td>
                    <td className="text-right tabular-nums text-slate-300">
                      <DrillValue to={{ set: 'aiCalls', filters: { account: a.accountId } }} label={`AI calls by ${a.email ?? a.accountId}`} expect={{ key: 'count', value: a.calls }} testId={`ai-account-calls-${a.accountId}`}>{num(a.calls)}</DrillValue>
                    </td>
                    <td className="text-right tabular-nums text-slate-300">{num(a.inputTokens)} / {num(a.outputTokens)}</td>
                    <td className="text-right tabular-nums text-slate-300">
                      <DrillValue to={{ set: 'aiCalls', filters: { account: a.accountId } }} label={`Anthropic cost of ${a.email ?? a.accountId}`} expect={{ key: 'rawCostUsd', value: a.rawCostUsd }}>{usd(a.rawCostUsd)}</DrillValue>
                    </td>
                    <td className="text-right tabular-nums text-slate-300">
                      <DrillValue to={{ set: 'aiCalls', filters: { account: a.accountId } }} label={`Billed to ${a.email ?? a.accountId}`} expect={{ key: 'billedUsd', value: a.billedUsd }}>{usd(a.billedUsd)}</DrillValue>
                    </td>
                    <td className="text-right tabular-nums text-slate-300">{marginPct(a.rawCostUsd, a.billedUsd)}</td>
                    <td className="text-right tabular-nums" data-testid={`ai-balance-${a.accountId}`}>
                      {bal === null ? (
                        <span className="text-slate-500">no credit row</span>
                      ) : (
                        <span className={bal > 0 ? 'text-emerald-300' : 'text-slate-400'}>{fmtCredits(bal)}</span>
                      )}
                      {a.buckets && a.buckets.length > 0 && (
                        <div className="text-[10px] text-slate-500">
                          {CREDIT_BUCKETS.map((b) => {
                            const sum = a.buckets!.filter((x) => x.kind === b.kind).reduce((s, x) => s + x.credits, 0)
                            return sum ? `${b.kind} ${num(sum)}` : null
                          })
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      )}
                    </td>
                    <td className="text-right">
                      <button
                        onClick={() => setOpen(a)}
                        className="px-2.5 py-1 rounded-md text-xs border border-accent/40 text-accent hover:bg-accent/10"
                        data-testid={`ai-credits-${a.accountId}`}
                      >
                        {session?.role === 'support' ? 'Credits' : 'Credits / grant'}
                      </button>
                    </td>
                  </ExpandableRow>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {open && (
        <CreditsModal
          accountId={open.accountId}
          title={accountName(open)}
          onClose={() => setOpen(null)}
          onGranted={() => void load()}
        />
      )}
    </div>
  )
}

const AI_ACCOUNT_COLUMNS: Column<RemoteAiAccount>[] = [
  { key: 'account', label: 'Account', value: (a) => `${a.displayName ?? ''} ${a.handle ?? ''} ${a.email ?? ''}`.trim(), filter: 'text' },
  { key: 'tier', label: 'Tier', value: (a) => String((a as { tier?: string }).tier ?? '') },
  { key: 'calls', label: 'Calls', value: (a) => a.calls, numeric: true, className: 'text-right' },
  { key: 'tokens', label: 'Tokens (in / out)', value: (a) => a.inputTokens + a.outputTokens, numeric: true, className: 'text-right' },
  { key: 'cost', label: 'Anthropic cost', value: (a) => a.rawCostUsd, numeric: true, className: 'text-right' },
  { key: 'billed', label: 'Billed', value: (a) => a.billedUsd, numeric: true, className: 'text-right' },
  { key: 'margin', label: 'Margin', value: (a) => a.billedUsd - a.rawCostUsd, numeric: true, className: 'text-right' },
  { key: 'balance', label: 'Balance', value: (a) => a.balanceUsd ?? 0, numeric: true, className: 'text-right' },
  { key: 'actions', label: '', filter: 'none', sortable: false, className: 'text-right' }
]

export default function AI(): JSX.Element {
  const [overview, setOverview] = useState<RemoteAiOverview | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void getAiOverview(30).then((res) => {
      if (cancelled) return
      if (res.ok) {
        setOverview(res.data.overview)
        setState('ready')
      } else {
        setError(res.error)
        setState('error')
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">PlexiBrain</div>
        <h1 className="text-2xl font-semibold mt-1">AI credits</h1>
        <div className="text-sm text-slate-400 mt-1">
          1 credit = $0.01 of billed usage. Billed = Anthropic list cost × markup. Credits are spent allowance first, then bonus, then purchased.
        </div>
      </div>

      {state === 'loading' && <div className="glass-card rounded-xl p-6 text-sm text-slate-400">Loading AI overview…</div>}
      {state === 'error' && <LoadError what="the AI overview" endpoint="GET /admin/ai-overview" error={error ?? 'unknown error'} />}
      {state === 'ready' && overview && (
        <>
          <OverviewSection overview={overview} />
          <SectionHead title="Allowance by tier" blurb="This period's monthly allowance per tier and how much of it was spent." />
          <TierAllowanceSection overview={overview} />
          <SectionHead title="Top-ups" blurb="One-off credit purchases." />
          <TopupSection overview={overview} />
        </>
      )}

      <SectionHead title="Usage log" blurb="The latest metered calls: model, tokens, what Anthropic charged and what was billed." />
      <UsageLogSection />

      <SectionHead title="Accounts" blurb="Lifetime spend and current credit balance per account. Open one to see its buckets and grant credits." />
      <AccountsSection />
    </div>
  )
}

// ── What each row opens to ──────────────────────────────────────────────────

const perCall = (v: number, calls: number): string => (calls ? usdFine(v / calls) : '—')
const share = (part: number, whole: number): string => (whole ? `${((part / whole) * 100).toFixed(1)}%` : '—')

function ModelDetail({ m, totals }: { m: RemoteAiModelRow; totals: RemoteAiOverview['totals'] }): JSX.Element {
  return (
    <DetailGrid
      facts={[
        ['Model', <span className="font-mono">{m.model}</span>],
        ['Calls', `${num(m.calls)} (${share(m.calls, totals.calls)} of all calls)`],
        ['Input tokens', num(m.inputTokens)],
        ['Output tokens', num(m.outputTokens)],
        ['Tokens per call', m.calls ? `${num(Math.round(m.inputTokens / m.calls))} in · ${num(Math.round(m.outputTokens / m.calls))} out` : null],
        ['Anthropic cost', `${usd(m.rawCostUsd)} (${perCall(m.rawCostUsd, m.calls)} a call)`],
        ['Billed', `${usd(m.billedUsd)} (${perCall(m.billedUsd, m.calls)} a call; ${share(m.billedUsd, totals.billedUsd)} of all billing)`],
        ['Credits debited', fmtCredits(usdToCredits(m.billedUsd))],
        ['Gross margin', `${marginPct(m.rawCostUsd, m.billedUsd)} (${usd(m.billedUsd - m.rawCostUsd)})`],
        ['Period', 'All time']
      ]}
    />
  )
}

function TierDetail({ t }: { t: RemoteAiTierUsage }): JSX.Element {
  const spec = TIER_LADDER.find((s) => s.id === t.tier)
  return (
    <DetailGrid
      facts={[
        ['Tier', tierName(t.tier)],
        ['Price', spec ? (spec.priceMonthly === null ? 'Custom' : `${usd(spec.priceMonthly)} a month per ${spec.unit}`) : null],
        ['Allowance per account', spec?.allowanceCredits != null ? `${num(spec.allowanceCredits)} credits a month${spec.unit === 'seat' ? ' per seat' : ''}` : 'Custom'],
        ['Allowance pooled', spec ? (spec.allowancePooled ? 'Yes, shared across the organisation' : 'No, held per person') : null],
        ['Models', spec ? (spec.models === 'all' ? 'All models' : spec.models.join(', ')) : null],
        ['Max tokens a reply', spec?.maxTokens != null ? num(spec.maxTokens) : 'Not fixed'],
        ['Accounts', num(t.accounts)],
        ['Allowance issued', fmtCredits(t.allowanceCredits)],
        ['Allowance used', `${fmtCredits(t.allowanceUsedCredits)} (${pct(t.allowanceUsedCredits, t.allowanceCredits)})`],
        ['Allowance left', fmtCredits(t.allowanceCredits - t.allowanceUsedCredits)],
        ['Bonus credits used', typeof t.bonusUsedCredits === 'number' ? fmtCredits(t.bonusUsedCredits) : 'Not reported'],
        ['Purchased credits used', typeof t.purchasedUsedCredits === 'number' ? fmtCredits(t.purchasedUsedCredits) : 'Not reported'],
        ['Anthropic cost', typeof t.rawCostUsd === 'number' ? usd(t.rawCostUsd) : 'Not reported'],
        ['Billed', typeof t.billedUsd === 'number' ? usd(t.billedUsd) : 'Not reported']
      ]}
      wide={spec ? [['About this tier', spec.summary]] : undefined}
    />
  )
}

function AccountDetail({ a, onCredits }: { a: RemoteAiAccount; onCredits: () => void }): JSX.Element {
  const bal = creditsOf(a.balance, a.balanceUsd)
  return (
    <DetailGrid
      facts={[
        ['Name', a.displayName],
        ['Handle', a.handle ? `@${a.handle}` : null],
        ['Email', a.email],
        ['Account id', <span className="font-mono">{a.accountId}</span>],
        ['Tier', a.tier ? tierName(a.tier) : 'Not reported'],
        ['Calls', num(a.calls)],
        ['Input tokens', num(a.inputTokens)],
        ['Output tokens', num(a.outputTokens)],
        ['Cache read tokens', num(a.cacheReadTokens)],
        ['Cache write tokens', num(a.cacheWriteTokens)],
        ['Anthropic cost', `${usd(a.rawCostUsd)} (${perCall(a.rawCostUsd, a.calls)} a call)`],
        ['Billed', `${usd(a.billedUsd)} (${fmtCredits(usdToCredits(a.billedUsd))})`],
        ['Gross margin', `${marginPct(a.rawCostUsd, a.billedUsd)} (${usd(a.billedUsd - a.rawCostUsd)})`],
        ['Credit balance', bal === null ? 'No credit row yet' : fmtCredits(bal)],
        typeof a.grantedUsd === 'number' && ['Granted (old dollar ledger)', usd(a.grantedUsd)],
        typeof a.spentUsd === 'number' && ['Spent (old dollar ledger)', usd(a.spentUsd)]
      ]}
    >
      <div className="w-full grid gap-1" data-testid={`ai-account-buckets-${a.accountId}`}>
        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400">Credit buckets</div>
        {!a.buckets || a.buckets.length === 0 ? (
          <div className="text-slate-500">{a.buckets ? 'None.' : 'Not reported by this server.'}</div>
        ) : (
          a.buckets.map((b, i) => {
            const meta = CREDIT_BUCKETS.find((x) => x.kind === b.kind)
            return (
              <div key={i} className="text-slate-300" title={meta?.rule}>
                {meta?.label ?? b.kind}: {fmtCredits(b.credits)}
                {b.expiresAt ? ` · expires ${dateTime(b.expiresAt)}` : ' · no expiry'}
                {b.source ? ` · from ${b.source}` : ''}
                {b.grantedAt ? ` · granted ${dateTime(b.grantedAt)}` : ''}
              </div>
            )
          })
        )}
      </div>
      <button className="btn-ghost text-xs" onClick={onCredits} data-testid={`ai-account-open-credits-${a.accountId}`}>
        Credit history
      </button>
      {a.email && (
        <Link className="btn-ghost text-xs" to={`/emails?tab=log&q=${encodeURIComponent(a.email)}`}>
          Emails sent to them
        </Link>
      )}
    </DetailGrid>
  )
}
